/* hs-eslint ignored failing-rules */

import { Record, Map as ImmutableMap, Set as ImmutableSet, List } from 'immutable';
import isEqual from 'hs-lodash/isEqual';
import AuditEventActionMetadata from 'automation-ui-management/records/AuditHistory/AuditEventActionMetadata';
import EnrollmentAuditEventTypes from 'automation-ui-management/records/AuditHistory/EnrollmentAuditEventTypes';
export const AuditEventKeys = {
  ASSOCIATED_BULK_ENROLLMENT: 'associatedBulkEnrollment',
  AUDIT_EVENT_TYPE: 'auditEventType',
  ALL_AUDIT_EVENT_TYPES: 'allAuditEventTypes',
  USER_ID: 'userId',
  TIMESTAMP: 'timestamp',
  REVISIONS: 'revisions',
  AUDIT_EVENT_ACTION_METADATA: 'auditEventActionMetadata',
  ENROLL_EXISTING_DECISION: 'enrollExistingDecision'
};
const MILLIS_PER_MINUTE = 60000;
export default class AuditEvent extends Record({
  [AuditEventKeys.ASSOCIATED_BULK_ENROLLMENT]: null,
  [AuditEventKeys.AUDIT_EVENT_TYPE]: null,
  [AuditEventKeys.ALL_AUDIT_EVENT_TYPES]: List(),
  [AuditEventKeys.USER_ID]: null,
  [AuditEventKeys.TIMESTAMP]: null,
  [AuditEventKeys.REVISIONS]: [],
  [AuditEventKeys.AUDIT_EVENT_ACTION_METADATA]: new AuditEventActionMetadata({}),
  [AuditEventKeys.ENROLL_EXISTING_DECISION]: 'NOT_APPLICABLE'
}, 'AuditEvent') {
  constructor(props) {
    const propMap = ImmutableMap(props);
    super(propMap);
  }
  getAssociatedBulkEnrollment() {
    return this[AuditEventKeys.ASSOCIATED_BULK_ENROLLMENT];
  }
  getEnrollExistingDecision() {
    return this[AuditEventKeys.ENROLL_EXISTING_DECISION];
  }
  getAuditEventType() {
    return this[AuditEventKeys.AUDIT_EVENT_TYPE];
  }
  getAllAuditEventTypes() {
    return this[AuditEventKeys.ALL_AUDIT_EVENT_TYPES];
  }
  getUserId() {
    return this[AuditEventKeys.USER_ID];
  }
  getTimestamp() {
    return this[AuditEventKeys.TIMESTAMP];
  }
  getRevisions() {
    return this[AuditEventKeys.REVISIONS];
  }
  getFirstRevision() {
    const revisions = this.getRevisions();
    return revisions.length > 0 ? revisions[0] : null;
  }
  getIsGroupedEvent() {
    const revisions = this.getRevisions();
    return revisions.length > 2;
  }
  getIsEnrollmentEvent() {
    return this.getAllAuditEventTypes().isSubset(EnrollmentAuditEventTypes);
  }
  getAuditEventActionMetadata() {
    return this[AuditEventKeys.AUDIT_EVENT_ACTION_METADATA];
  }
  shouldMergeWith(auditEvent) {
    const timestampDiffInMinutes = Math.abs(Math.floor((this.getTimestamp() - auditEvent.getTimestamp()) / MILLIS_PER_MINUTE));
    const isWithinTimeThreshold = timestampDiffInMinutes < 60;
    const auditEventTypesMatch = this.getAuditEventType() === auditEvent.getAuditEventType() || this.getIsEnrollmentEvent() && auditEvent.getIsEnrollmentEvent();
    const hasSameEnrollExistingDecision = this.getEnrollExistingDecision() === auditEvent.getEnrollExistingDecision();
    const hasSameUpdatedUserId = this.getUserId() === auditEvent.getUserId();
    return isWithinTimeThreshold && auditEventTypesMatch && hasSameEnrollExistingDecision && hasSameUpdatedUserId;
  }
  mergeAuditEvent(auditEvent) {
    let primaryAuditEvent;
    let secondaryAuditEvent;
    if (this.getTimestamp() > auditEvent.getTimestamp()) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      primaryAuditEvent = this;
      secondaryAuditEvent = auditEvent;
    } else {
      primaryAuditEvent = auditEvent;
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      secondaryAuditEvent = this;
    }
    const auditEventType = primaryAuditEvent.getAuditEventType();
    const allAuditEventTypes = ImmutableSet([
    // @ts-expect-error apparently immutable Lists don't have `Symbol.iterator`, but leaving to prevert regressions
    ...primaryAuditEvent.getAllAuditEventTypes(), ...secondaryAuditEvent.getAllAuditEventTypes()]);
    const userId = primaryAuditEvent.getUserId();
    const timestamp = primaryAuditEvent.getTimestamp();
    const revisions = ImmutableSet([...primaryAuditEvent.getRevisions(), ...secondaryAuditEvent.getRevisions()]).toArray().sort((revision, nextRevision) => nextRevision.timestamp - revision.timestamp);
    const auditEventActionMetadata = this.getAuditEventActionMetadata().mergeAuditEventActionMetadata(auditEvent.getAuditEventActionMetadata());

    // Note: Because we don't merge events with different enroll existing decisions (see the `shouldMergeWith` method), we can
    // grab this value from either the `primaryAuditEvent` or `secondaryAuditEvent`- it's arbitrary
    const enrollExistingDecision = primaryAuditEvent.getEnrollExistingDecision();
    let associatedBulkEnrollment = null;
    const primaryAssociatedBulkEnrollment = primaryAuditEvent.getAssociatedBulkEnrollment();
    const secondaryAssociatedBulkEnrollment = secondaryAuditEvent.getAssociatedBulkEnrollment();
    if (primaryAssociatedBulkEnrollment && secondaryAssociatedBulkEnrollment) {
      const mergedSize = (primaryAssociatedBulkEnrollment.size || 0) + (secondaryAssociatedBulkEnrollment.size || 0);

      // @ts-expect-error isEqual expects to arguments, but we have "&&"" instead of "," here. We can revisit this in a smaller changeset
      const mergedMetadata = isEqual(primaryAssociatedBulkEnrollment.metadata && secondaryAssociatedBulkEnrollment.metadata) ? primaryAssociatedBulkEnrollment.metadata :
      // TODO: Revisit this as well
      {};
      associatedBulkEnrollment = Object.assign({}, primaryAssociatedBulkEnrollment, {
        size: mergedSize,
        metadata: mergedMetadata
      });
    } else {
      associatedBulkEnrollment = primaryAuditEvent.getAssociatedBulkEnrollment() || secondaryAuditEvent.getAssociatedBulkEnrollment();
    }
    const mergedAuditEvent = new AuditEvent({
      [AuditEventKeys.ASSOCIATED_BULK_ENROLLMENT]: associatedBulkEnrollment,
      [AuditEventKeys.AUDIT_EVENT_TYPE]: auditEventType,
      [AuditEventKeys.ALL_AUDIT_EVENT_TYPES]: allAuditEventTypes,
      [AuditEventKeys.USER_ID]: userId,
      [AuditEventKeys.TIMESTAMP]: timestamp,
      [AuditEventKeys.REVISIONS]: revisions,
      [AuditEventKeys.AUDIT_EVENT_ACTION_METADATA]: auditEventActionMetadata,
      [AuditEventKeys.ENROLL_EXISTING_DECISION]: enrollExistingDecision
    });
    return mergedAuditEvent;
  }
  updateAuditEventActionMetadata(auditEventActionMetadata) {
    return this.set(AuditEventKeys.AUDIT_EVENT_ACTION_METADATA, auditEventActionMetadata);
  }
  updateEventType(auditEventType) {
    return this.set(AuditEventKeys.AUDIT_EVENT_TYPE, auditEventType);
  }
}